import React, { useRef, useEffect, useMemo, useState } from "react";
import * as d3 from "d3";
import PropTypes from "prop-types";

import ChartWrapper from "../chartWrapper/index.js";
import Key from "../key/index.js";

import { stackedScaleDomain, stackedTransformedData } from "../utils/index.js";

const StackedBarChart = ({ data, dimensions }) => {
  const [keyHeight, setKeyHeight] = useState(0);

  const chartContainer = useRef(null);
  const keyContainer = useRef(null);

  const { width, height, margin } = dimensions;

  const groups = data[0].values.map((d) => d.x);
  const subGroups = data.map((d) => d.name);

  const transformedData = useMemo(() => {
    return stackedTransformedData(data, groups);
  }, [data, groups]);

  const stackedData = useMemo(() => {
    return d3.stack().keys(subGroups)(transformedData);
  }, [transformedData, subGroups]);

  const yScale = d3.scaleBand().domain(groups).range([0, height]).padding(0.1);

  const xScaleDomain = useMemo(() => {
    return stackedScaleDomain(stackedData);
  }, [stackedData]);

  const xScale = d3
    .scaleLinear()
    .domain(xScaleDomain)
    .nice() // Optionally, adjust domain to extend it to nice round values
    .range([0, width]);

  const colorScale = d3
    .scaleOrdinal()
    .domain(data.map((d) => d.name))
    .range(d3.schemeCategory10);

  useEffect(() => {
    if (data && chartContainer.current) {
      if (keyContainer.current) {
        setKeyHeight(
          keyContainer.current.getBoundingClientRect().height + margin.top
        );
      }

      const svg = d3
        .select(chartContainer.current)
        .html("") // Clear the container in case of re-render
        .attr("width", width + margin.left + margin.right)
        .attr("height", height + (margin.top + keyHeight) + margin.bottom)
        .append("g")
        .attr("class", "stacked-bar-container")
        .attr(
          "transform",
          `translate(${margin.left},${margin.top + keyHeight})`
        );

      // add stacked horizontal bars here
      svg
        .selectAll(".stacked-bar-container")
        .data(stackedData)
        .enter()
        .append("g")
        .attr("fill", (d) => colorScale(d.key))
        .selectAll("rect")
        .data((d) => d)
        .enter()
        .append("rect")
        .attr("y", (d) => yScale(d.data.category))
        .attr("x", (d) => xScale(d[0]))
        .attr("height", yScale.bandwidth())
        .attr("width", (d) => xScale(d[1]) - xScale(d[0]));
    }
  }, [data, dimensions, xScale, yScale, colorScale]); // Include all dependencies used within the effect

  return (
    <ChartWrapper
      axes={{
        xScale,
        yScale,
        height,
        width,
        margin: { ...margin, top: margin.top + keyHeight }
      }}
      height={height + (margin.top + keyHeight) + margin.bottom}
      width={width + margin.left + margin.right}
    >
      <Key
        forwardedRef={keyContainer}
        data={data}
        colorScale={colorScale}
        margin={margin}
        width={width}
      />
      <g ref={chartContainer} />
    </ChartWrapper>
  );
};

StackedBarChart.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      values: PropTypes.arrayOf(
        PropTypes.shape({
          x: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
            .isRequired,
          y: PropTypes.number.isRequired
        })
      ).isRequired
    })
  ).isRequired,
  dimensions: PropTypes.shape({
    width: PropTypes.number.isRequired,
    height: PropTypes.number.isRequired,
    margin: PropTypes.shape({
      top: PropTypes.number,
      right: PropTypes.number,
      bottom: PropTypes.number,
      left: PropTypes.number
    }).isRequired
  }).isRequired
};

StackedBarChart.defaultProps = {
  dimensions: {
    width: 500,
    height: 300,
    margin: { top: 20, right: 30, bottom: 30, left: 50 }
  }
};

export default StackedBarChart;
